import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, useMatch, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import CodeIcon from '@mui/icons-material/Code';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import StorageIcon from '@mui/icons-material/Storage';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getProject, getPhaseProgress } from '../../utils/api';

const drawerWidth = 280;
const collapsedWidth = 64;

const PHASE_COLOR = '#9C27B0';

const realizeProcesses = [
  { id: 'ricefw-list', name: 'RICEFW List', description: 'Generate and download RICEFW list, Functional Specs, and Technical Specs from backlog data', icon: <ListAltIcon />, path: 'ricefw-list' },
  // { id: 'configuration', name: 'Configuration', description: 'System configuration and setup documentation', icon: <TuneIcon />, path: 'configuration' },
  // { id: 'development', name: 'Development', description: 'Custom development tracking and documentation', icon: <CodeIcon />, path: 'development' },
  { id: 'uat-test-scripts', name: 'Test Scripts', description: 'Generate and download test scripts for scope items', icon: <FactCheckIcon />, path: 'uat-test-scripts' },
  // { id: 'data-migration', name: 'Data Migration', description: 'Data migration planning and execution tracking', icon: <StorageIcon />, path: 'data-migration' },
];

const RealizePhaseLayout = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [subphaseProgress, setSubphaseProgress] = useState({});
  const [selectedProcess, setSelectedProcess] = useState(null);

  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

  useEffect(() => {
    if (!projectId || projectId === ':projectId') return;
    getPhaseProgress(projectId).then(res => {
      if (res.success && res.subphase_progress) setSubphaseProgress(res.subphase_progress);
    });
  }, [projectId]);

  useEffect(() => {
    const pathSegment = location.pathname.split('/').pop();
    const process = realizeProcesses.find(p => p.path === pathSegment);
    setSelectedProcess(process || null);
  }, [location.pathname]);

  useEffect(() => {
    const selectedProject = localStorage.getItem('selectedProject');
    if (selectedProject) {
      try {
        const project = JSON.parse(selectedProject);
        const selectedProjectId = project.id ? project.id.toString() : null;
        if (selectedProjectId && projectId !== selectedProjectId) {
          const currentPath = window.location.pathname.split('/').slice(3).join('/');
          navigate(`/projects/${selectedProjectId}/${currentPath}`, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing selected project:', e);
      }
    }
  }, [projectId, navigate]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        if (projectId && projectId !== ':projectId') {
          const response = await getProject(projectId);
          if (response.success) setProject(response.project);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', p: 1, minHeight: 56 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <BuildCircleIcon sx={{ mr: 1, color: PHASE_COLOR }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: PHASE_COLOR }}>
              Realize Phase
            </Typography>
          </Box>
        )}
        {isMobile ? (
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon />
          </IconButton>
        ) : (
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>
      <Divider />
      <List>
        {realizeProcesses.map((process) => (
          <Tooltip key={process.id} title={collapsed ? process.name : ''} placement="right">
            <ListItem
              button
              selected={selectedProcess?.id === process.id}
              onClick={() => {
                navigate(`/projects/${projectId}/realize/${process.path}`);
                setMobileOpen(false);
              }}
              sx={{
                '&.Mui-selected': { backgroundColor: 'rgba(156, 39, 176, 0.12)', borderRight: collapsed ? 0 : 3, borderColor: PHASE_COLOR },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(156, 39, 176, 0.20)' },
                '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.08)' },
                mb: 1,
                mx: collapsed ? 0.5 : 1,
                borderRadius: 1,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2
              }}
            >
              <ListItemIcon sx={{ color: selectedProcess?.id === process.id ? PHASE_COLOR : 'inherit', minWidth: collapsed ? 0 : 56, justifyContent: 'center' }}>
                {process.icon}
              </ListItemIcon>
              {!collapsed && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ListItemText
                  primary={process.name}
                  secondary={process.description}
                  primaryTypographyProps={{ fontWeight: selectedProcess?.id === process.id ? 600 : 400, color: selectedProcess?.id === process.id ? PHASE_COLOR : 'inherit' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    sx={{ mb: 0.5 }}

                  />
                  <LinearProgress
                    variant="determinate"
                    value={subphaseProgress[process.path] ?? 0}
                    sx={{ height: 3, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.08)' }}
                  />
                </Box>

              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" color="error">Project not found</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="nav"
        sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 }, zIndex: (theme) => theme.zIndex.appBar - 1, transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }) }}
        aria-label="realize phase processes"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, mt: '100px' },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              mt: '100px',
              height: 'calc(100vh - 100px)',
              position: 'fixed',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen })
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {isMobile && !mobileOpen && (
        <IconButton
          color="primary"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{ position: 'fixed', left: 16, top: 80, zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: 0 },
          mt: 2,
          transition: theme.transitions.create(['width', 'margin'], { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen })
        }}
      >
        <Container maxWidth="xl">
          <Outlet context={{ project, selectedProcess }} />
        </Container>
      </Box>
    </Box>
  );
};

export default RealizePhaseLayout;
